import type { User, UserCredential, UserInfo } from 'firebase/auth'
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  getAdditionalUserInfo,
  reload,
  sendPasswordResetEmail,
  sendEmailVerification as sendVerificationEmailWithFirebase,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
} from 'firebase/auth'
import { firebaseAuth } from '@/config/firebase'
import { getLocale } from '@/config/locale'
import { clearPendingAuthEmail, persistPendingAuthEmail, readPendingAuthEmail } from './storage'

const googleProvider = new GoogleAuthProvider()

const firebaseSupportedLocales = new Set(['nl', 'en', 'fr', 'es', 'de'])

function normalizeEmail(value: string | null | undefined) {
  const trimmed = value?.trim()
  return trimmed ? trimmed : null
}

function resolveFirebaseLanguage() {
  const locale = getLocale()
  return firebaseSupportedLocales.has(locale) ? locale : 'en'
}

export function syncFirebaseAuthLanguage() {
  firebaseAuth.languageCode = resolveFirebaseLanguage()
}

export function resolveCredentialEmail(credential: UserCredential) {
  const directEmail = normalizeEmail(credential.user?.email)
  if (directEmail) return directEmail

  for (const provider of credential.user?.providerData ?? []) {
    const providerEmail = normalizeEmail(provider.email)
    if (providerEmail) return providerEmail
  }

  const profile =
    getAdditionalUserInfo(credential)?.profile ?? ({} as Record<string, unknown>)
  const rawEmail = typeof profile.email === 'string' ? profile.email : null

  return normalizeEmail(rawEmail)
}

function firstProviderEmail(user: User) {
  for (const provider of user.providerData as UserInfo[]) {
    const providerEmail = normalizeEmail(provider.email)
    if (providerEmail) return providerEmail
  }

  return null
}

export async function resolveRegistrationEmail(currentUser: User) {
  const directEmail = normalizeEmail(currentUser.email)
  if (directEmail) return directEmail

  const providerEmail = firstProviderEmail(currentUser)
  if (providerEmail) return providerEmail

  await reload(currentUser)

  const reloadedUser = firebaseAuth.currentUser
  if (reloadedUser) {
    const reloadedDirectEmail = normalizeEmail(reloadedUser.email)
    if (reloadedDirectEmail) return reloadedDirectEmail

    const reloadedProviderEmail = firstProviderEmail(reloadedUser)
    if (reloadedProviderEmail) return reloadedProviderEmail
  }

  return readPendingAuthEmail()
}

async function persistCredentialEmail(credential: UserCredential) {
  persistPendingAuthEmail(resolveCredentialEmail(credential))
}

export async function signInWithEmail(email: string, password: string) {
  syncFirebaseAuthLanguage()
  return signInWithEmailAndPassword(firebaseAuth, email.trim(), password)
}

export async function signUpWithEmail(email: string, password: string) {
  syncFirebaseAuthLanguage()
  const credential = await createUserWithEmailAndPassword(
    firebaseAuth,
    email.trim(),
    password,
  )

  persistPendingAuthEmail(email)
  await sendVerificationEmailWithFirebase(credential.user)
  return credential
}

export async function signInWithGoogle() {
  syncFirebaseAuthLanguage()
  const credential = await signInWithPopup(firebaseAuth, googleProvider)
  await persistCredentialEmail(credential)
  return credential
}

export async function sendVerificationEmail() {
  const currentUser = firebaseAuth.currentUser
  if (!currentUser) {
    throw new Error('Geen ingelogde gebruiker beschikbaar.')
  }

  syncFirebaseAuthLanguage()
  await sendVerificationEmailWithFirebase(currentUser)
}

export async function checkEmailVerification() {
  const currentUser = firebaseAuth.currentUser
  if (!currentUser) return false

  await reload(currentUser)

  const refreshedUser = firebaseAuth.currentUser
  if (!refreshedUser?.emailVerified) return false

  await refreshedUser.getIdToken(true)
  return true
}

export async function sendPasswordReset(email: string) {
  syncFirebaseAuthLanguage()
  await sendPasswordResetEmail(firebaseAuth, email.trim())
}

export async function signOut() {
  clearPendingAuthEmail()
  await firebaseSignOut(firebaseAuth)
}
